<template>
    <div>
        <p>
            Type
            <b-select v-model="type_choice" :options="[
                { text: 'Projet', value: 'projet' },
                { text: 'Base de données', value: 'db' },
                { text: 'Mail', value: 'mail' }
            ]" />
        </p>

        <p>
            Projet
            <v-select
                v-model="project"
                :options="project_list"
                label="text"
                :reduce="(option) => option.text" />
        </p>

        <p>
            Intervalle
            <div class="d-flex align-items-center flex-wrap" style="gap: 8px;">
                <b-input v-model="repeatInterval" placeholder="ex: 0 8 * * *" style="max-width: 320px;" @input="on_repeat_interval_manual_edit" />
                <b-button size="sm" variant="outline-secondary" @click="toggle_cron_builder">
                    <i class="fas fa-magic"></i> Générateur cron
                </b-button>
            </div>
            <b-card v-if="show_cron_builder" bg-variant="light" class="mt-2 mb-2">
                <CronLight :key="cron_builder_key" v-model="cron_builder_value" locale="fr" :periods="cron_periods_builder" :custom-locale="cron_locale_fr" />
            </b-card>
        </p>
        <p>
            Date
            <b-input type="datetime-local" v-model="nextRunAt" />
        </p>

        <!-- ===== PROJET ===== -->
        <template v-if="type_choice === 'projet'">
            <p>
                <b-checkbox v-model="only_one">Ne pas déclencher si il y a déjà un traitement</b-checkbox>
            </p>

            <div class="mt-2">
                <strong>Tableau</strong>
                <small class="text-muted d-block mb-1">Données injectées comme fichier initial du scénario (une ligne par objet) — saisie manuelle ou import d'un csv/xlsx</small>
                <edit_tabulator ref="json_tabulator" :extern_file="true" :style="'max-width: ' + (size - 100) + 'px'" />
            </div>

            <div class="mt-2">
                <strong>Fichiers</strong>
                <b-table-simple small bordered class="mt-1">
                    <b-thead>
                        <b-tr>
                            <b-th>Nom</b-th>
                            <b-th>Url</b-th>
                            <b-th></b-th>
                        </b-tr>
                    </b-thead>
                    <b-tbody>
                        <b-tr v-for="(row, idx) in files_data" :key="'agf' + idx">
                            <b-td>
                                <b-input v-model="row.name" size="sm" />
                            </b-td>
                            <b-td>
                                <b-input v-model="row.url" size="sm" />
                            </b-td>
                            <b-td>
                                <b-button size="sm" variant="danger" @click="files_data.splice(idx, 1)">✕</b-button>
                            </b-td>
                        </b-tr>
                    </b-tbody>
                </b-table-simple>
                <b-button size="sm" @click="files_data.push({ name: '', type: 'import', url: '', format: '' })">+ Ajouter un fichier</b-button>
            </div>
        </template>

        <!-- ===== BASE DE DONNÉES ===== -->
        <template v-if="type_choice === 'db'">
            <p>
                Table
                <b-input v-model="table" />
            </p>
            <p>
                Conditions
                <b-input v-model="where" />
            </p>
            <p>
                Colonne de prise en compte
                <b-input v-model="column_process" />
            </p>
        </template>

        <!-- ===== MAIL ===== -->
        <template v-if="type_choice === 'mail'">
            <p>
                Récupérer pièces avec les extensions suivantes
                <b-input v-model="extension" placeholder="ex: pdf,csv" />
            </p>
            <p>
                Email
                <b-input v-model="user" />
            </p>
            <p>
                Mot de passe
                <b-input type="password" v-model="password" />
            </p>
            <p>
                Host
                <b-input v-model="host" />
            </p>
            <p>
                Port
                <b-input type="number" v-model.number="port" />
            </p>
        </template>
    </div>
</template>

<script>
    import uniqueId from "lodash.uniqueid";
    import edit_tabulator from "../edit_tabulator.vue";
    import { cron_locale_fr, cron_periods_fr } from "../../utils/cron_locale_fr.js";
    import { cron_names_to_numbers } from "../../utils/cron_names_to_numbers.js";
    import { order_periods_for_value } from "../../utils/cron_period_order.js";

    export default {
        name: "agenda_step",
        components: { edit_tabulator },
        props: {
            data: { default: () => [] },
            files: { default: () => [] },
            client: { default: null },
            size: { default: 1000 },
            step_position: { default: 0 },
            project_list: { default: () => [] }
        },
        data() {
            return {
                id: uniqueId(""),

                type_choice: "projet",
                project: "",
                repeatInterval: "",
                nextRunAt: "",
                show_cron_builder: false,
                cron_periods_builder: cron_periods_fr,
                cron_builder_key: 0,

                // Projet
                only_one: false,
                json: [],
                files_data: [],

                // Base de données
                table: "",
                where: "",
                column_process: "",

                // Mail
                extension: "",
                user: "",
                password: "",
                host: "",
                port: 993
            };
        },
        computed: {
            cron_periods_fr() {
                return cron_periods_fr;
            },
            cron_locale_fr() {
                return cron_locale_fr;
            },
            cron_builder_value: {
                get() {
                    return cron_names_to_numbers(this.repeatInterval);
                },
                set(value) {
                    this.repeatInterval = value;
                }
            }
        },
        methods: {
            toggle_cron_builder() {
                if (!this.show_cron_builder) {
                    this.cron_periods_builder = order_periods_for_value(cron_periods_fr, this.repeatInterval);
                    this.cron_builder_key++;
                }
                this.show_cron_builder = !this.show_cron_builder;
            },
            on_repeat_interval_manual_edit(value) {
                if (!this.show_cron_builder) return;
                this.cron_periods_builder = order_periods_for_value(cron_periods_fr, value);
                this.cron_builder_key++;
            },
            save() {
                const base = {
                    _type: this.type_choice,
                    project: this.project,
                    repeatInterval: this.repeatInterval,
                    nextRunAt: this.nextRunAt
                };

                if (this.type_choice === "db") {
                    return Object.assign({}, base, {
                        table: this.table,
                        where: this.where,
                        column_process: this.column_process
                    });
                }
                if (this.type_choice === "mail") {
                    return Object.assign({}, base, {
                        extension: this.extension,
                        user: this.user,
                        password: this.password,
                        host: this.host,
                        port: this.port
                    });
                }
                return Object.assign({}, base, {
                    only_one: this.only_one,
                    json: this.$refs.json_tabulator ? this.$refs.json_tabulator.get_data() : this.json,
                    files: this.files_data
                });
            },
            async load(data) {
                if (!data) data = {};
                this.type_choice = data._type || "projet";
                this.project = data.project || "";
                this.repeatInterval = data.repeatInterval || "";
                this.nextRunAt = data.nextRunAt || "";

                this.only_one = !!data.only_one;
                this.json = Array.isArray(data.json) ? data.json : [];
                this.files_data = Array.isArray(data.files) ? data.files : [];

                this.table = data.table || "";
                this.where = data.where || "";
                this.column_process = data.column_process || "";

                this.extension = data.extension || "";
                this.user = data.user || "";
                this.password = data.password || "";
                this.host = data.host || "";
                this.port = data.port !== undefined ? data.port : 993;

                await this.$nextTick();
                if (this.$refs.json_tabulator)
                    await this.$refs.json_tabulator.set_data(this.json);
            }
        }
    };
</script>

<style scoped>
</style>
